import type { LatLng } from '../api'

const R = 6371008.8

/** Distance en mètres entre deux points (haversine). */
export function distanceM(a: LatLng, b: LatLng): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

export function lengthM(coords: LatLng[]): number {
  let total = 0
  for (let i = 1; i < coords.length; i += 1) total += distanceM(coords[i - 1], coords[i])
  return total
}

export function formatLength(m: number): string {
  return m >= 1000 ? `${(m / 1000).toFixed(2)} km` : `${Math.round(m)} m`
}

export function midpoint(a: LatLng, b: LatLng): LatLng {
  return { lat: (a.lat + b.lat) / 2, lng: (a.lng + b.lng) / 2 }
}

export function toLngLat(c: LatLng): [number, number] {
  return [c.lng, c.lat]
}

/** Emprise [[ouest, sud], [est, nord]] d'une liste de points, ou null. */
export function boundsOf(points: LatLng[]): [[number, number], [number, number]] | null {
  if (!points.length) return null
  let w = Infinity
  let s = Infinity
  let e = -Infinity
  let n = -Infinity
  for (const p of points) {
    w = Math.min(w, p.lng)
    e = Math.max(e, p.lng)
    s = Math.min(s, p.lat)
    n = Math.max(n, p.lat)
  }
  return [
    [w, s],
    [e, n],
  ]
}

/** Tous les points d'une géométrie GeoJSON (pour cadrer la carte). */
export function geometryPoints(geom: GeoJSON.Geometry | null | undefined): LatLng[] {
  if (!geom) return []
  const out: LatLng[] = []
  const walk = (c: unknown): void => {
    if (Array.isArray(c) && typeof c[0] === 'number') out.push({ lng: c[0] as number, lat: c[1] as number })
    else if (Array.isArray(c)) c.forEach(walk)
  }
  if (geom.type === 'GeometryCollection') geom.geometries.forEach((g) => out.push(...geometryPoints(g)))
  else walk((geom as { coordinates: unknown }).coordinates)
  return out
}

/**
 * Point le plus proche sur une ligne : indice du segment, position (0 à 1) sur ce segment
 * et le point lui-même. Calcul à plat, corrigé pour la latitude (assez précis à l'échelle d'un parc).
 */
export function nearestOnLine(coords: LatLng[], p: LatLng): { seg: number; t: number; point: LatLng; distM: number } {
  const k = Math.cos((p.lat * Math.PI) / 180)
  let best = { seg: 0, t: 0, point: coords[0], distM: Infinity }
  for (let i = 0; i < coords.length - 1; i += 1) {
    const a = coords[i]
    const b = coords[i + 1]
    const ax = a.lng * k
    const bx = b.lng * k
    const px = p.lng * k
    const dx = bx - ax
    const dy = b.lat - a.lat
    const len2 = dx * dx + dy * dy
    const t = len2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (p.lat - a.lat) * dy) / len2)) : 0
    const point = { lat: a.lat + t * dy, lng: a.lng + t * (b.lng - a.lng) }
    const d = distanceM(point, p)
    if (d < best.distM) best = { seg: i, t, point, distM: d }
  }
  return best
}

/** Portion d'une ligne entre deux points cliqués près d'elle (dans l'ordre de la ligne). */
export function sliceBetween(coords: LatLng[], from: LatLng, to: LatLng): LatLng[] {
  let a = nearestOnLine(coords, from)
  let b = nearestOnLine(coords, to)
  if (a.seg > b.seg || (a.seg === b.seg && a.t > b.t)) [a, b] = [b, a]
  const out: LatLng[] = [a.point]
  for (let i = a.seg + 1; i <= b.seg; i += 1) out.push(coords[i])
  out.push(b.point)
  return out.filter((c, i) => i === 0 || distanceM(c, out[i - 1]) > 0.05)
}

/** Les points d'une ligne ou multi-ligne GeoJSON, mis bout à bout. */
export function lineCoords(geom: GeoJSON.LineString | GeoJSON.MultiLineString | null | undefined): LatLng[] {
  if (!geom) return []
  const parts = geom.type === 'LineString' ? [geom.coordinates] : geom.coordinates
  return parts.flat().map(([lng, lat]) => ({ lat, lng }))
}

/** Clé d'un point (au cm près) : deux chemins qui partagent cette clé sont reliés (jonction). */
export function pointKey(c: LatLng): string {
  return `${c.lat.toFixed(7)},${c.lng.toFixed(7)}`
}

/**
 * Jonctions : points présents dans au moins deux chemins. Chaque chemin compte une fois,
 * même s'il repasse par le même point.
 */
export function junctionKeys(lines: LatLng[][]): Set<string> {
  const count = new Map<string, number>()
  for (const line of lines) {
    for (const k of new Set(line.map(pointKey))) count.set(k, (count.get(k) ?? 0) + 1)
  }
  return new Set([...count].filter(([, n]) => n > 1).map(([k]) => k))
}

/** Croisement de deux segments (à plat, corrigé pour la latitude), avec la position sur chacun. */
export function segmentIntersection(a1: LatLng, a2: LatLng, b1: LatLng, b2: LatLng): { point: LatLng; t: number; u: number } | null {
  const k = Math.cos((a1.lat * Math.PI) / 180)
  const x1 = a1.lng * k
  const y1 = a1.lat
  const x2 = a2.lng * k
  const y2 = a2.lat
  const x3 = b1.lng * k
  const y3 = b1.lat
  const x4 = b2.lng * k
  const y4 = b2.lat
  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4)
  if (Math.abs(den) < 1e-14) return null
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den
  const u = -((x1 - x2) * (y1 - y3) - (y1 - y2) * (x1 - x3)) / den
  if (t < 0 || t > 1 || u < 0 || u > 1) return null
  return { point: { lat: a1.lat + t * (a2.lat - a1.lat), lng: a1.lng + t * (a2.lng - a1.lng) }, t, u }
}

/** Où deux chemins devraient être reliés et ne le sont pas encore. */
export type JunctionCandidate =
  | { kind: 'cross'; point: LatLng; seg: number; otherId: number; otherSeg: number }
  | { kind: 'myEnd'; point: LatLng; end: number; otherId: number; otherSeg: number }
  | { kind: 'otherEnd'; point: LatLng; seg: number; otherId: number }

const NEAR_M = 5

/**
 * Croisements du chemin en cours avec les autres, et extrémités qui touchent presque une autre
 * ligne (à moins de 5 m) sans y être reliées. Les points déjà partagés sont ignorés.
 */
export function junctionCandidates(geom: LatLng[], others: { id: number; coordinates: LatLng[] }[]): JunctionCandidate[] {
  const out: JunctionCandidate[] = []
  if (geom.length < 2) return out
  const mine = new Set(geom.map(pointKey))
  const isVertexNear = (line: LatLng[], p: LatLng) => line.some((c) => distanceM(c, p) < 1)
  for (const o of others) {
    const line = o.coordinates
    if (line.length < 2) continue
    const shared = line.some((c) => mine.has(pointKey(c)))
    for (let i = 0; i < geom.length - 1; i += 1) {
      for (let j = 0; j < line.length - 1; j += 1) {
        const hit = segmentIntersection(geom[i], geom[i + 1], line[j], line[j + 1])
        if (!hit) continue
        // Déjà relié à cet endroit (point commun aux deux lignes).
        if (shared && isVertexNear(geom, hit.point) && isVertexNear(line, hit.point)) continue
        out.push({ kind: 'cross', point: hit.point, seg: i, otherId: o.id, otherSeg: j })
      }
    }
    for (const end of [0, geom.length - 1]) {
      const p = geom[end]
      if (line.some((c) => pointKey(c) === pointKey(p))) continue
      const near = nearestOnLine(line, p)
      if (near.distM < NEAR_M && near.distM > 0.05) out.push({ kind: 'myEnd', point: near.point, end, otherId: o.id, otherSeg: near.seg })
    }
    for (const p of [line[0], line[line.length - 1]]) {
      if (mine.has(pointKey(p))) continue
      const near = nearestOnLine(geom, p)
      if (near.distM < NEAR_M && near.t > 0.001 && near.t < 0.999) out.push({ kind: 'otherEnd', point: p, seg: near.seg, otherId: o.id })
    }
  }
  // Un seul repère par endroit.
  return out.filter((c, i) => out.findIndex((d) => distanceM(d.point, c.point) < 1) === i)
}
